import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import {
  segmentsQuery,
  presetsQuery,
  useCreateSegment,
  useDeleteSegment,
} from '../lib/segments.js';

export function SegmentsPage() {
  const segments = useQuery(segmentsQuery);
  const presets = useQuery(presetsQuery);
  const create = useCreateSegment();
  const remove = useDeleteSegment();
  const navigate = useNavigate();

  async function applyPreset(key: string) {
    const preset = presets.data?.items.find((p) => p.key === key);
    if (!preset) return;
    const result = await create.mutateAsync({
      name: preset.name,
      description: preset.description,
      definition: preset.definition,
    });
    await navigate({ to: '/segmente/$id', params: { id: result.segment.id } });
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>Segmente</h1>
          <p className="muted text-sm">
            Regelbasierte Kontaktgruppen. Werden bei jeder Verwendung neu ausgewertet.
          </p>
        </div>
        <Link to="/segmente/neu" className="btn" data-variant="primary">
          Segment anlegen
        </Link>
      </header>

      {segments.data && segments.data.items.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Kontakte
                  </th>
                  <th scope="col">Zuletzt ausgewertet</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {segments.data.items.map((segment) => (
                  <tr key={segment.id}>
                    <td>
                      <Link to="/segmente/$id" params={{ id: segment.id }}>
                        {segment.name}
                      </Link>
                      {segment.description && (
                        <div className="muted text-sm">{segment.description}</div>
                      )}
                    </td>
                    <td data-numeric style={{ textAlign: 'right' }}>
                      {/*
                        Keine Zahl ist besser als eine veraltete: nach jeder
                        Aenderung an der Definition wird der Wert verworfen,
                        bis erneut ausgewertet wurde.
                      */}
                      {segment.memberCount ?? <span className="muted">—</span>}
                    </td>
                    <td className="muted text-sm">
                      {segment.lastEvaluatedAt
                        ? new Date(segment.lastEvaluatedAt).toLocaleString('de-CH')
                        : 'noch nie'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Button
                        variant="ghost"
                        onClick={() => remove.mutate(segment.id)}
                        aria-label={`Segment ${segment.name} löschen`}
                      >
                        Löschen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Vorlagen</h2>
          <p className="muted text-sm">
            Häufige Fälle als fertiger Regelbaum. Nach dem Einsetzen frei bearbeitbar.
          </p>
          <div className="preset-grid">
            {presets.data?.items.map((preset) => (
              <button
                key={preset.key}
                className="preset"
                onClick={() => applyPreset(preset.key)}
                disabled={create.isPending}
              >
                <strong>{preset.name}</strong>
                <span className="muted text-sm">{preset.description}</span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
